import {
    createContext,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * The groups of toolbar buttons a tool rail holds, top to bottom: the
 * step's own tools, then its operations.
 */
export type ToolRailGroup = 'tools' | 'ops';

/**
 * The elements the tool rail layout renders into: the slot of each rail
 * group, and the layer over the views that the tool options dock and the
 * draggable panels render into (`panels`).
 */
type ToolRailSlot = ToolRailGroup | 'panels';

type SlotElements = Readonly<Record<ToolRailSlot, HTMLElement | null>>;
type SlotRefs = Readonly<
    Record<ToolRailSlot, (element: HTMLElement | null) => void>
>;

interface ToolRailSlotsValue {
    /** Each slot's element, or null while none is mounted. */
    elements: SlotElements;
    /** Ref callbacks that make an element a slot. Stable for the provider's life. */
    refs: SlotRefs;
}

const NO_SLOTS: SlotElements = {
    tools: null,
    ops: null,
    panels: null,
};

const ToolRailSlotsContext = createContext<ToolRailSlotsValue>({
    elements: NO_SLOTS,
    refs: { tools: () => {}, ops: () => {}, panels: () => {} },
});

/**
 * True below a {@link ToolRailPortal}: the toolbar buttons there sit in a
 * vertical rail rather than in the toolbar that renders them.
 */
const InToolRailContext = createContext(false);

/**
 * Holds the tool rail's slot elements and the panels layer for everything
 * below it, so a toolbar rendered in one part of the layout can place its
 * buttons in a rail, and its options in a dock, shown in another. Buttons
 * in a {@link ToolRailPortal} show only while a rail mounts their group.
 * Without a panels layer, tool options, operation panels and draggable
 * panels do not show.
 */
export function ToolRailSlotsProvider({ children }: { children: ReactNode }) {
    const [elements, setElements] = useState<SlotElements>(NO_SLOTS);
    const refs = useMemo(() => {
        const refOf = (slot: ToolRailSlot) => (element: HTMLElement | null) =>
            setElements((current) =>
                current[slot] === element
                    ? current
                    : { ...current, [slot]: element }
            );
        return {
            tools: refOf('tools'),
            ops: refOf('ops'),
            panels: refOf('panels'),
        };
    }, []);
    const value = useMemo(() => ({ elements, refs }), [elements, refs]);
    return (
        <ToolRailSlotsContext.Provider value={value}>
            {children}
        </ToolRailSlotsContext.Provider>
    );
}

/** The element of `slot`, or null while none is mounted. */
export function useToolRailSlot(slot: ToolRailSlot): HTMLElement | null {
    return useContext(ToolRailSlotsContext).elements[slot];
}

/** Ref callback that makes an element the element of `slot`. */
export function useToolRailSlotRef(
    slot: ToolRailSlot
): (element: HTMLElement | null) => void {
    return useContext(ToolRailSlotsContext).refs[slot];
}

/** Whether the calling toolbar button sits in a tool rail. */
export function useInToolRail(): boolean {
    return useContext(InToolRailContext);
}

/**
 * Renders `children` into the slot of `group`, and nothing while no tool
 * rail shows that group. They keep the context of the toolbar that renders
 * them, but remount if the slot's element changes. State that must outlive
 * the rail belongs outside the portal.
 */
export function ToolRailPortal({
    group,
    children,
}: {
    group: ToolRailGroup;
    children: ReactNode;
}) {
    const slot = useToolRailSlot(group);
    if (!slot) {
        return null;
    }
    return createPortal(
        <InToolRailContext.Provider value={true}>
            {children}
        </InToolRailContext.Provider>,
        slot
    );
}
